import { useQuote } from '../../contexts/QuoteContext';
import { EditableField } from '../../components/EditableField/index';

interface QuotePreviewProps {
  quote: {
    title: string;
    customer: string;
    totalPrice: number;
    lineItems: Array<{
      description: string;
      price: number;
      quantity: number;
      discount?: number;
    }>;
  };
  onSave: () => void;
  onStartOver: () => void;
  isSaving: boolean;
}

export const QuotePreview = ({ quote, onSave, onStartOver, isSaving }: QuotePreviewProps) => {
  const { 
    currentQuote,
    setCurrentQuote,
    updateQuoteField, 
    updateLineItem, 
    addLineItem, 
    removeLineItem 
  } = useQuote();

  // Initialize the quote in context if not already set
  if (!currentQuote) {
    console.log('Setting initial quote in context:', quote);
    
    // Ensure all line items have IDs before setting in context
    const quoteWithIds = {
      ...quote,
      lineItems: (quote.lineItems || []).map((item: any, index: number) => ({
        ...item,
        id: item.id || `item_${Date.now()}_${index}_${Math.random().toString(36).substring(2, 9)}`
      }))
    };
    
    console.log('Quote with IDs:', quoteWithIds);
    setCurrentQuote(quoteWithIds);
  }

  const displayQuote = currentQuote || quote;
  console.log('Current quote in preview:', displayQuote);
  return (
    <div className="quote-preview">
      <div className="preview-header">
        <h2>Quote Preview</h2>
        <p>Review the generated quote below and click save if it looks good!</p>
      </div>

      <div className="preview-card">
        <div className="quote-summary">
          <div className="summary-row">
            <span className="label">Quote Title:</span>
            <EditableField
              value={displayQuote.title || ''}
              onSave={(value) => updateQuoteField('title', value)}
              placeholder="Enter quote title"
            />
          </div>
          <div className="summary-row">
            <span className="label">Customer:</span>
            <EditableField
              value={displayQuote.customer || ''}
              onSave={(value) => updateQuoteField('customer', value)}
              placeholder="Enter customer name"
            />
          </div>
          <div className="summary-row total-price">
            <span className="label">Total Price:</span>
            <span className="value price calculated-price">
              ${(displayQuote.totalPrice || 0).toLocaleString()}
            </span>
            <span className="auto-calc-label">(Auto-calculated)</span>
          </div>
        </div>

        <div className="line-items-section">
          <div className="section-header">
            <h3>Line Items</h3>
            <button onClick={addLineItem} className="add-line-item-button">
              ➕ Add Item
            </button>
          </div>
          
          {displayQuote.lineItems && displayQuote.lineItems.length > 0 ? (
            <div className="editable-line-items">
              {displayQuote.lineItems.map((item: any, index: number) => (
                <div key={index} className="editable-line-item">
                  <div className="line-item-fields">
                    <div className="field-group">
                      <label>Description</label>
                      <EditableField
                        value={item.description || ''}
                        onSave={(value) => updateLineItem(index, 'description', value)}
                        placeholder="Item description"
                        className="description-field"
                      />
                    </div>
                    <div className="field-group">
                      <label>Price</label>
                      <EditableField
                        value={item.price || 0}
                        onSave={(value) => updateLineItem(index, 'price', value)}
                        type="number"
                        prefix="$"
                        placeholder="0"
                        className="price-field"
                      />
                    </div>
                    <div className="field-group">
                      <label>Quantity</label>
                      <EditableField
                        value={item.quantity || 1}
                        onSave={(value) => updateLineItem(index, 'quantity', value)}
                        type="number"
                        placeholder="1"
                        className="quantity-field"
                      />
                    </div>
                    <div className="field-group">
                      <label>Discount %</label>
                      <EditableField
                        value={item.discount || 0}
                        onSave={(value) => updateLineItem(index, 'discount', value)}
                        type="number"
                        placeholder="0"
                        className="discount-field"
                      />
                    </div>
                  </div>
                  <button 
                    onClick={() => removeLineItem(index)}
                    className="remove-line-item-button"
                    title="Remove this item"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-line-items">
              <p>No line items yet</p>
              <button onClick={addLineItem} className="add-first-item-button">
                Add First Item
              </button>
            </div>
          )}
        </div>

        {/* Show success message after save */}
        {currentQuote?._id && (
          <div className="save-success">
            <p>✅ Quote saved successfully! You can continue editing or view all quotes.</p>
          </div>
        )}
      </div>

      <div className="preview-actions">
        <button 
          onClick={onStartOver} 
          className="secondary-button"
          disabled={isSaving}
        >
          ↺ Start Over
        </button>
        <button 
          onClick={onSave} 
          className="save-button"
          disabled={isSaving}
        >
          {isSaving ? (
            <>⏳ Saving...</>
          ) : (
            <>💾 Save Quote</>
          )}
        </button>
      </div>
    </div>
  );
};